import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { hospitalDoctors } from '@/stores/authStore'
import { useMedicalRecordStore } from '@/stores/medicalRecordStore'
import VoiceInput from '@/components/VoiceInput'
import AIDoctorAdvice from '@/components/AIDoctorAdvice'

export default function MedicalRecordEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { getRecord, updateRecord } = useMedicalRecordStore()
  
  const record = getRecord(id || '')
  
  const [formData, setFormData] = useState({
    recordDate: '',
    doctorId: '',
    subjectiveNote: '',
    objectiveNote: '',
    assessmentNote: '',
    planNote: '',
    additionalNote: '',
  })

  const [doctors, setDoctors] = useState<Array<{ id: string; name: string; specialty?: string }>>([])
  const [showVoiceInput, setShowVoiceInput] = useState(false)
  const [showAIDoctor, setShowAIDoctor] = useState(false)
  const [notification, setNotification] = useState<{ type: 'success' | 'info', message: string } | null>(null)

  useEffect(() => {
    if (record) {
      setFormData({
        recordDate: record.recordDate,
        doctorId: record.doctorId,
        subjectiveNote: record.subjectiveNote,
        objectiveNote: record.objectiveNote,
        assessmentNote: record.assessmentNote,
        planNote: record.planNote,
        additionalNote: record.additionalNote,
      })
      setDoctors(hospitalDoctors[record.hospitalId] || [])
    }
  }, [record])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const selectedDoctor = doctors.find(d => d.id === formData.doctorId)
    
    updateRecord(id || '', {
      recordDate: formData.recordDate,
      doctorId: formData.doctorId,
      doctorName: selectedDoctor?.name || record?.doctorName || '',
      subjectiveNote: formData.subjectiveNote,
      objectiveNote: formData.objectiveNote,
      assessmentNote: formData.assessmentNote,
      planNote: formData.planNote,
      additionalNote: formData.additionalNote,
    })
    
    console.log('Medical record updated:', formData)
    navigate(`/medical-records/${id}`)
  }

  // 音声入力からのテキスト処理
  const handleTranscript = (text: string) => {
    setFormData(prev => ({
      ...prev,
      additionalNote: prev.additionalNote ? `${prev.additionalNote}\n\n【音声入力】\n${text}` : `【音声入力】\n${text}`
    }))
    showNotificationMsg('info', '文字起こし内容を備考に追記しました')
  }

  // SOAP形式の自動入力
  const handleSOAPExtracted = (soap: { s: string; o: string; a: string; p: string }) => {
    setFormData(prev => ({
      ...prev,
      subjectiveNote: prev.subjectiveNote ? `${prev.subjectiveNote}\n${soap.s}` : soap.s,
      objectiveNote: prev.objectiveNote ? `${prev.objectiveNote}\n${soap.o}` : soap.o,
      assessmentNote: prev.assessmentNote ? `${prev.assessmentNote}\n${soap.a}` : soap.a,
      planNote: prev.planNote ? `${prev.planNote}\n${soap.p}` : soap.p,
    }))
    showNotificationMsg('success', 'SOAP形式でカルテに反映しました')
  }

  const showNotificationMsg = (type: 'success' | 'info', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  if (!record) {
    return (
      <div className="aip-main__content">
        <div className="error-message">
          <span className="material-symbols-outlined">error</span>
          <p>診療記録が見つかりません</p>
          <Link to="/medical-records" className="aip-button aip-button--outline">
            一覧に戻る
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <Link to={`/medical-records/${id}`} className="aip-button aip-button--icon">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <h1 className="aip-main__header-title">診療記録 編集</h1>
        </div>
      </header>

      <div className="aip-main__content">
        {/* 通知 */}
        {notification && (
          <div className={`notification notification--${notification.type}`}>
            <span className="material-symbols-outlined">
              {notification.type === 'success' ? 'check_circle' : 'info'}
            </span>
            {notification.message}
          </div>
        )}

        <div className="edit-layout">
          <form onSubmit={handleSubmit} className="form-container">
            <div className="form-header">
              <h2 className="form-title">診療記録を編集</h2>
              <div className="form-meta">
                <span className="form-meta__item">
                  <span className="material-symbols-outlined">local_hospital</span>
                  {record.hospitalName}
                </span>
                <span className="form-meta__item">
                  <span className="material-symbols-outlined">person</span>
                  {record.patientName} ({record.researchId})
                </span>
              </div>
            </div>

            <div className="form-body">
              {/* 基本情報 */}
              <section className="form-section">
                <h3 className="form-section__title">基本情報</h3>
                <div className="form-grid">
                  <div className="aip-floating-field">
                    <label className="aip-floating-field__label" htmlFor="recordDate">
                      診療日<span className="required">※必須</span>
                    </label>
                    <input
                      className="aip-floating-field__control aip-input-text"
                      type="date"
                      id="recordDate"
                      name="recordDate"
                      value={formData.recordDate}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="aip-floating-field">
                    <label className="aip-floating-field__label" htmlFor="doctorId">
                      担当医師<span className="required">※必須</span>
                    </label>
                    <select
                      className="aip-floating-field__control aip-select"
                      id="doctorId"
                      name="doctorId"
                      value={formData.doctorId}
                      onChange={handleChange}
                      required
                    >
                      <option value="">医師を選択</option>
                      {doctors.map((doctor) => (
                        <option key={doctor.id} value={doctor.id}>
                          {doctor.name} {doctor.specialty && `（${doctor.specialty}）`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              {/* 音声入力セクション */}
              <section className="form-section">
                <div className="form-section__header">
                  <h3 className="form-section__title">
                    <span className="material-symbols-outlined">mic</span>
                    音声入力
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowVoiceInput(!showVoiceInput)}
                    className="toggle-button"
                  >
                    {showVoiceInput ? '閉じる' : '開く'}
                    <span className="material-symbols-outlined">
                      {showVoiceInput ? 'expand_less' : 'expand_more'}
                    </span>
                  </button>
                </div>
                
                {showVoiceInput && (
                  <div className="voice-section">
                    <p className="voice-description">
                      診察中の会話を録音し、AIが自動的にSOAP形式でカルテに追記します
                    </p>
                    <VoiceInput
                      onTranscript={handleTranscript}
                      onSOAPUpdate={handleSOAPExtracted}
                      placeholder="マイクボタンを押して診察中の会話を録音してください"
                    />
                  </div>
                )}
              </section>

              {/* SOAP記録 */}
              <section className="form-section">
                <h3 className="form-section__title">
                  <span className="material-symbols-outlined">edit_note</span>
                  SOAP記録
                </h3>

                <div className="soap-field">
                  <label className="soap-label soap-label--s">
                    <span className="soap-badge">S</span>
                    主観的情報（Subjective）
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="subjectiveNote"
                    value={formData.subjectiveNote}
                    onChange={handleChange}
                    rows={4}
                    placeholder="患者の訴え、症状、自覚症状など"
                  />
                </div>

                <div className="soap-field">
                  <label className="soap-label soap-label--o">
                    <span className="soap-badge">O</span>
                    客観的情報（Objective）
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="objectiveNote"
                    value={formData.objectiveNote}
                    onChange={handleChange}
                    rows={4}
                    placeholder="診察所見、検査結果、バイタルサインなど"
                  />
                </div>

                <div className="soap-field">
                  <label className="soap-label soap-label--a">
                    <span className="soap-badge">A</span>
                    評価（Assessment）
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="assessmentNote"
                    value={formData.assessmentNote}
                    onChange={handleChange}
                    rows={4}
                    placeholder="診断、病状評価、問題点の分析など"
                  />
                </div>

                <div className="soap-field">
                  <label className="soap-label soap-label--p">
                    <span className="soap-badge">P</span>
                    計画（Plan）
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="planNote"
                    value={formData.planNote}
                    onChange={handleChange}
                    rows={4}
                    placeholder="治療計画、処方、次回予約、患者指導など"
                  />
                </div>

                <div className="soap-field">
                  <label className="soap-label">
                    <span className="material-symbols-outlined">note_add</span>
                    その他・備考
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="additionalNote"
                    value={formData.additionalNote}
                    onChange={handleChange}
                    rows={3}
                    placeholder="その他の記録、メモなど"
                  />
                </div>
              </section>
            </div>

            {/* フッター */}
            <div className="form-footer">
              <Link to={`/medical-records/${id}`} className="aip-button aip-button--outline">
                キャンセル
              </Link>
              <button type="submit" className="aip-button aip-button--solid-fill">
                <span className="material-symbols-outlined">save</span>
                保存
              </button>
            </div>
          </form>

          {/* AIドクターサイドバー */}
          <aside className="ai-sidebar">
            <div className="ai-sidebar__header">
              <h3>AIアシスタント</h3>
              <button
                type="button"
                onClick={() => setShowAIDoctor(!showAIDoctor)}
                className="toggle-button"
              >
                {showAIDoctor ? '閉じる' : '開く'}
              </button>
            </div>
            
            {showAIDoctor && (
              <AIDoctorAdvice
                patientInfo={{
                  name: record.patientName,
                  age: record.age,
                  gender: record.gender,
                  diseaseName: record.diseaseName,
                  transplantCount: record.transplantCount,
                }}
                soapData={{
                  s: formData.subjectiveNote,
                  o: formData.objectiveNote,
                  a: formData.assessmentNote,
                  p: formData.planNote,
                }}
              />
            )}
          </aside>
        </div>
      </div>

      <style>{`
        .notification {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          border-radius: var(--border-radius-md);
          margin-bottom: var(--spacing-md);
          animation: slideIn 0.3s ease;
        }

        .notification--success {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .notification--info {
          background-color: var(--color-blue50);
          color: var(--color-blue600);
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .edit-layout {
          display: grid;
          grid-template-columns: 1fr 350px;
          gap: var(--spacing-lg);
        }

        .form-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .form-header {
          padding: var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }

        .form-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: var(--spacing-sm);
        }

        .form-meta {
          display: flex;
          gap: var(--spacing-lg);
          flex-wrap: wrap;
        }

        .form-meta__item {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .form-meta__item .material-symbols-outlined {
          font-size: 18px;
        }

        .form-body {
          padding: var(--spacing-lg);
        }

        .form-section {
          margin-bottom: var(--spacing-xl);
        }

        .form-section__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .form-section__title {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--border-color);
        }

        .form-section__title .material-symbols-outlined {
          color: var(--color-blue600);
        }

        .toggle-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-xs) var(--spacing-sm);
          background: none;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-sm);
          font-size: 0.75rem;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .toggle-button:hover {
          background-color: var(--color-solid-gray50);
        }

        .voice-section {
          margin-top: var(--spacing-md);
        }

        .voice-description {
          font-size: 0.875rem;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-md);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-md);
        }

        .aip-floating-field__label {
          display: block;
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-xs);
        }

        .aip-input-text,
        .aip-select,
        .aip-textarea {
          width: 100%;
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: white;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .aip-input-text:focus,
        .aip-select:focus,
        .aip-textarea:focus {
          outline: none;
          border-color: var(--color-blue500);
          box-shadow: 0 0 0 3px var(--color-blue100);
        }

        .aip-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .required {
          color: var(--color-alert);
          font-size: 0.75rem;
          margin-left: var(--spacing-xs);
        }

        .soap-field {
          margin-bottom: var(--spacing-lg);
        }

        .soap-label {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: var(--spacing-sm);
        }

        .soap-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          font-weight: 700;
          color: white;
        }

        .soap-label--s .soap-badge { background-color: #0891b2; }
        .soap-label--o .soap-badge { background-color: #059669; }
        .soap-label--a .soap-badge { background-color: #d97706; }
        .soap-label--p .soap-badge { background-color: #7c3aed; }

        .form-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-sm);
          padding: var(--spacing-lg);
          border-top: 1px solid var(--border-color);
        }

        .ai-sidebar {
          position: sticky;
          top: 80px;
          height: fit-content;
        }

        .ai-sidebar__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--spacing-md);
        }

        .ai-sidebar__header h3 {
          font-size: 1rem;
          font-weight: 600;
        }

        .error-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing-xl);
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          text-align: center;
        }

        .error-message .material-symbols-outlined {
          font-size: 64px;
          color: var(--color-alert);
          margin-bottom: var(--spacing-md);
        }

        @media (max-width: 1200px) {
          .edit-layout {
            grid-template-columns: 1fr;
          }

          .ai-sidebar {
            position: static;
          }
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  )
}
